import { getApiToken } from '../helpers/helper';
import { Module } from '../hooks/useActiveModule';

import { _request, _requestWithRetry, BASE_URL } from './request';

type GenerateSpeechTextRequest = {
  ceremonyId: number;
};

type GenerateCeremonyDesignerTextRequest = {
  ceremonyId: number | string;
  selectedModule: Module;
  saveResultOnBE?: boolean;
};

type GenerateSpeechTextResponse = {
  success: boolean;
  text: string;
};

type GenerateVowsAnalysisResponse = {
  success: boolean;
  data: {
    tone: {
      couple1: string;
      couple2: string;
    }
    length: {
      couple1: number;
      couple2: number;
      suggestion: string;
    };
    suggestions: string[];
  };
};

// For a slow generation the backend sends 200 early to keep the connection
// open, so a failure arrives as { success: false } instead of a 401.
const failIfUnsuccessful = (responseBody: { success: boolean }) => {
  if (!responseBody.success) throw responseBody;
};

export const generateVowsAnalysis = async (
  body: GenerateSpeechTextRequest
): Promise<GenerateVowsAnalysisResponse> => {
  const responseBody = await _requestWithRetry<GenerateVowsAnalysisResponse>({
    url: `ai/vowbuilder-analysis`,
    method: 'POST',
    body,
  });
  return responseBody;
};

export const generateToastbuilderText = async (
  body: GenerateSpeechTextRequest
): Promise<GenerateSpeechTextResponse> => {
  const responseBody = await _requestWithRetry<GenerateSpeechTextResponse>(
    {
      url: `ai/toastbuilder`,
      method: 'POST',
      body,
    },
    { checkResult: failIfUnsuccessful }
  );
  return responseBody;
};

export const generateVowbuilderText = async (
  body: GenerateSpeechTextRequest
) => {
  const url = `${BASE_URL}ai/vowbuilder`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getApiToken()}`,
    },
    body: JSON.stringify(body),
  });

  return response;
};

export const generateWelcomeToastbuilderText = async (
  body: GenerateSpeechTextRequest
): Promise<GenerateSpeechTextResponse> => {
  const responseBody = await _requestWithRetry<GenerateSpeechTextResponse>(
    {
      url: `ai/welcometoastbulider`,
      method: 'POST',
      body,
    },
    { checkResult: failIfUnsuccessful }
  );
  return responseBody;
};

export const generateCeremonyScriptText = async (
  body: GenerateCeremonyDesignerTextRequest
) => {
  const responseBody = await _request<GenerateSpeechTextResponse>({
    url: `ai/ceremony-script`,
    method: 'POST',
    body,
  });
  failIfUnsuccessful(responseBody);
  return responseBody;
};

export const generateCeremonyScriptStreamText = async (
  body: GenerateCeremonyDesignerTextRequest
) => {
  const url = `${BASE_URL}ai/ceremony-script`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getApiToken()}`,
    },
    body: JSON.stringify(body),
  });

  return response;
};

export const generateFineTuneCeremonyBuilderScript = async (
  body: GenerateCeremonyDesignerTextRequest & {
    prompt: string;
  }
): Promise<GenerateSpeechTextResponse> => {
  const responseBody = await _request<GenerateSpeechTextResponse>({
    url: `ai/fine-tune-cb-script`,
    method: 'POST',
    body,
  });
  failIfUnsuccessful(responseBody);
  return responseBody;
}